/**
 * Version constants for the persisted document model and the command layer.
 *
 * Three numbers move independently:
 *
 * - {@link COMMANDS_VERSION} — the package's own version, reported in sync
 *   handshakes so a Durable Object can refuse a client it cannot replay.
 * - {@link RECIPE_SCHEMA_VERSION} — stamped into every {@link RecipeDoc}.
 *   Bumped whenever a persisted recipe field changes shape; migrations key off
 *   it.
 * - {@link WORKSPACE_SCHEMA_VERSION} — stamped into {@link WorkspaceState},
 *   covering the workspace envelope (settings, equipment profiles).
 *
 * The catalog's own version is *not* here: it is recorded per snapshotted
 * ingredient in {@link CatalogRef.catalogVersion} (PLAN §6).
 */

/** The `@beerwright/commands` package version. */
export const COMMANDS_VERSION = '0.1.0';

/**
 * Schema version stamped into every persisted recipe document.
 *
 * **3** — three changes have landed on this stamp, and it holds all of them.
 *
 * *One extract scale.* Until now the interchange importer wrote a file's
 * `YIELD` into `ppgOverride`, a field the document reserves for a yield the
 * *brewer* measured, so a workspace with both imported and hand-built recipes
 * carried two definitions of extract at once and `bridge/actuals.ts` took a
 * median across them. `liftFermentableYield` lifts every such row onto the
 * document's own `extractFineGrindDryBasisPercent` and nulls the override,
 * exactly — the PPG core derives does not move. Nothing walks batches to do
 * it: a batch's `recipeSnapshot` *is* a `recipeDocSchema`, so the lift reaches
 * frozen snapshots and named recipe versions on the same read that reaches the
 * live recipe.
 *
 * *A link to one of the brewer's own ingredients.* Every ingredient row gained
 * `ingredientId` (PLAN-INGREDIENT-LINKS A1) — a *required* nullable key, but
 * one whose field schema reads absence as `null`, so a recipe written under
 * **2** parses it without a lift of its own. `WORKSPACE_SCHEMA_VERSION` does
 * not move for it.
 *
 * **These two share a stamp deliberately, and it must not be split.**
 * `migrateRecipeExtractScale` gates on `stamped >= RECIPE_SCHEMA_VERSION`,
 * against this constant rather than a literal 3. So bumping to **4** for the
 * link would re-lift every recipe already stamped 3 — and from 3 on, a
 * non-null `ppgOverride` is a yield the brewer typed, which the lift would
 * convert and null. A fourth stamp for a documentary change would therefore
 * delete measurements. The link needed no bump; it rides along on 3.
 *
 * *A target said in one breath.* The water section then gained `targetRule`, a
 * calcium floor and a sulfate-to-chloride balance beside the existing
 * `targetProfile` (PLAN-WATER-RULES D-WR1). It rides along on 3 for exactly the
 * reason the link does, and it is cheaper still: declared
 * `.nullable().default(null)`, a recipe written before rules existed parses
 * with no target rule — the truth for every one of them — so there is nothing
 * to lift. The `.default()` `kettleTargetPh` was denied is safe here because
 * the water section has no patch schema for a default to survive `.partial()`
 * into; see `document/water.ts`.
 *
 * **2** — the water section gained `kettleTargetPh`, a *required* key rather
 * than a defaulted one (see `document/water.ts` for why the document model
 * refuses `.default()` on anything a patch schema can reach). A recipe written
 * under **1** is lifted on the way in by `migrateRecipeWater`, which fills the
 * key with `null` — "no kettle target chosen", which is the truth for every one
 * of them.
 *
 * **1** — the original document.
 */
export const RECIPE_SCHEMA_VERSION = 3;

/**
 * Schema version stamped into every persisted batch document.
 *
 * **3** — `measurements` gained `kettle`: the pre-boil pH reading, the reading
 * after the kettle dose, and what went in. Required keys, not defaulted ones,
 * so a batch written under an earlier stamp is lifted on the way in by
 * `migrateBatchMeasurements`, which fills all three with `null`.
 *
 * **1** — introduced with brew-day mode and batch logging. Independent of
 * {@link RECIPE_SCHEMA_VERSION} even though a batch *contains* a recipe: the
 * contained copy carries its own stamp, so a batch written today records which
 * recipe schema its snapshot is in and a future recipe migration can walk into
 * batches without guessing.
 *
 * Recipe schema 3 is that migration, and it needed no walker at all: because
 * `recipeSnapshot` is declared as `recipeDocSchema`, the lift is part of
 * reading a batch. This stamp does not move for it — the batch document's own
 * shape did not change, and bumping it would drop every client's cached copy to
 * gain nothing.
 */
export const BATCH_SCHEMA_VERSION = 3;

/**
 * Schema version stamped into every persisted inventory item.
 *
 * **2** — a lot gained `ingredientId` (PLAN-INGREDIENT-LINKS A1), read as `null`
 * when absent for the reason `RECIPE_SCHEMA_VERSION` **3** gives, so a lot
 * written under **1** parses untouched.
 *
 * **1** — introduced with the V1 inventory (stock levels, "can I brew this?",
 * shopping list). Independent of the other three for the same reason
 * {@link BATCH_SCHEMA_VERSION} is: an item is its own document and carries its
 * own stamp.
 */
export const INVENTORY_SCHEMA_VERSION = 2;

/**
 * Schema version stamped into every persisted custom ingredient.
 *
 * **1** — introduced with the per-user fermentable/hop/yeast library. Independent
 * of the other stamps for the same reason {@link INVENTORY_SCHEMA_VERSION} is:
 * an entry is its own document and carries its own version.
 */
export const CUSTOM_INGREDIENT_SCHEMA_VERSION = 1;

/**
 * Schema version stamped into every persisted named recipe snapshot.
 *
 * **1** — introduced with recipe version history. Independent of
 * {@link RECIPE_SCHEMA_VERSION} for the same reason {@link BATCH_SCHEMA_VERSION}
 * is: the contained recipe copy carries its own stamp.
 */
export const RECIPE_SNAPSHOT_SCHEMA_VERSION = 1;

/**
 * Schema version stamped into the persisted workspace envelope.
 *
 * **4** — V1 added `inventory`. (**3** added `batches`, the brew log; **2**
 * added `waterProfiles`, the saved-water-profile library.)
 *
 * Version history added `recipeSnapshots` **without** a bump, which is not an
 * oversight: it is declared `.default({})`, so a v4 snapshot still validates and
 * simply arrives with no named versions — the same backward-compatible addition
 * `settings.massFormat` and `batch.inventoryDeductedAt` made. A bump would drop
 * every client's IndexedDB copy and force a full re-sync to gain nothing. The
 * rule is therefore: **a new required key bumps this; a defaulted one does
 * not.**
 *
 * The workspace schema is a `strictObject` with every key required, so a v3
 * snapshot cannot validate against v4 and must not be read as if it could. The
 * client keys its IndexedDB snapshot on this constant, so the bump reads as "no
 * saved workspace" and the v3 record stays on disk untouched; the Durable
 * Object treats a snapshot that fails validation as absent and rebuilds from
 * its command log, which is the authority anyway.
 */
export const WORKSPACE_SCHEMA_VERSION = 4;
